<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Content from './Content.svelte';
	import { hasRoleAreaManager, hasRoleCompanyManager, hasRoleSysAdmin, signout, userSession } from './shared/stores';
	import Impersonazione from './views/protected/admin/Impersonazione/Impersonazione.svelte';
	import NewsButton from './views/protected/client/News/NewsButton.svelte';
	import Area from './shared/models/Area/Area';
	import { onMount } from 'svelte';

	export let expanded = true;

	const currentYear = new Date().getFullYear();
	const lastYear = new Date().getFullYear() - 1;

	let currentArea;
	let isImpersonateEnabled = false;
	let companyLogo: string | null = null;

	let [areaData, loadingArea, errorArea] = [null, null, null];

	$: if ($userSession?.areaId) {
        [areaData, loadingArea, errorArea] = Area.get($userSession.areaId);
    }

    $: if (areaData) {
        currentArea = $areaData;
        isImpersonateEnabled = currentArea && currentArea.impersonateEnabled;
    }

	async function getCompanyLogo() {
        if ($userSession?.aziendaEnteId) {
            try {
                const response = await fetch(`api/aziendaente/getlogo/${$userSession.aziendaEnteId}`);
                if (response.ok) {
                    const result = await response.json();
                    if (result.response && result.response.content) {
                        companyLogo = `data:${result.response.mime};base64,${result.response.content}`;
                    }
                }
            } catch (error) {
                console.error('Error loading company logo:', error);
            }
        } else {
			companyLogo = null;
		}
    }

	$: {
        if ($userSession) {
            getCompanyLogo();
        } else {
            companyLogo = null;
        }
    }

    onMount(() => {
        getCompanyLogo();
    });
</script>

<div class="flex h-screen">
	<div class="flex flex-col flex-1 overflow-hidden">
		<div class="fixed z-20 w-full">
			<header class="flex items-center justify-between bg-[#e5e7eb]">
				<div class="flex justify-start">
					<img src="./assets/images/logo.png" alt="" />
					<button
						on:click="{() => (expanded = !expanded)}"
						class="ml-1.5 mt-[0.6rem] w-8 rounded-md bg-transparent p-2 focus:bg-transparent"
					>
						<svg
							class="h-4 w-4 text-gray-600 {expanded
								? '-rotate-180 transform transition-transform'
								: '0 transform transition-transform'}"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
						</svg>
					</button>
				</div>
				<div class="h-14 flex justify-end pr-4">
					{#if $userSession}
						<div class="flex items-center space-x-3">
							{#if ($hasRoleSysAdmin || ($hasRoleAreaManager && isImpersonateEnabled)) || $userSession?.isImpersonating}
								<Impersonazione />
							{/if}
							<!-- Notification Button -->
							{#if !$hasRoleSysAdmin}
								<NewsButton />
							{/if}
						</div>
					{/if}
				</div>
			</header>
		</div>
		<div class="flex h-[95%]">
			<!-- Sidebar -->
			{#if $userSession}
				<nav role="presentation" on:click="{() => (expanded = true)}" on:keydown="{() => (expanded = true)}" class="z-10 flex h-full">
					<div class="flex w-full px-6 py-8 mx-auto">
						<div class="bg-gray-50 flex flex-col flex-auto flex-shrink-0 min-h-screen antialiased text-gray-800">
							<div
								class="fixed top-14 left-0 flex flex-col {expanded ? 'w-64' : 'w-16'} h-[calc(100vh-3.5rem)] bg-white overflow-y-auto"
								class:border-r="{$userSession}"
							>
								<div class="flex-grow overflow-hidden">
									<div class="flex flex-col py-4 space-y-1">
										<div>
											<a
												href="#/"
												class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
											>
												<span class="inline-flex items-center justify-center ml-4">
													<span class="material-icons">home</span>
												</span>
												<span class="bg-gree ml-2 text-sm tracking-wide truncate">{$_('sidebar.home')}</span>
											</a>
										</div>
										{#if $hasRoleSysAdmin || $hasRoleAreaManager || $hasRoleCompanyManager}
											<div class="{expanded ? 'group' : ''}">
												<span
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">info</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.informations')}</span>
												</span>
												{#if $hasRoleSysAdmin || $hasRoleAreaManager || $hasRoleCompanyManager}
													<a
														href="#/sedi"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">apartment</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.informations.locations')}</span
														>
													</a>
												{/if}
												{#if $hasRoleSysAdmin}
													<a
														href="#/aree"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">map</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.informations.areas')}</span>
													</a>
												{/if}
												{#if $hasRoleAreaManager || $hasRoleSysAdmin}
													<a
														href="#/layers"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">layers</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.informations.layer')}</span>
													</a>
													<a
														href="#/aziendeenti"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">apartment</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.informations.companies/entities')}</span
														>
													</a>
													<a
														href="#/scuole"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">school</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.informations.schools')}</span>
													</a>
												{/if}
												<a
													href="#/map"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">location_on</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.informations.map')}</span>
												</a>
												<a
													href="#/news"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">newspaper</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">News</span>
												</a>
											</div>
											{#if $hasRoleSysAdmin || $hasRoleAreaManager || $hasRoleCompanyManager}
											<div class="{expanded ? 'group' : ''}">
												<span
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">supervised_user_circle</span>
													</span>
													{#if $hasRoleSysAdmin}
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.employees/staff')}</span>
													{:else if $userSession?.scuola}
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.staff')}</span>
													{:else}
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.employees')}</span>
													{/if}
												</span>
												<a
													href="#/indirizzidipendenti"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">person_pin_circle</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate"
														>{$_('sidebar.employees/staff.addresses_import')}</span
													>
												</a>
												<!--
												<a
													href="#/capcomunidipendenti"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">reduce_capacity</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate"
														>{$_('sidebar.employees/staff.addresses_export')}</span
													>
												</a>
											-->
											</div>
											{#if $hasRoleSysAdmin}
												<div class="{expanded ? 'group' : ''}">
													<span
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">analytics</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.measures')}</span>
													</span>
													<a
														href="#/categoriemisura"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">category</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.measures.measure_categories')}</span
														>
													</a>
													<a
														href="#/tipomisura"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">show_chart</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.measures.measure_type')}</span>
													</a>
													<a
														href="#/assi"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">line_axis</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.measures.axes')}</span>
													</a>
													<a
														href="#/azionicomunicazione"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">cell_tower</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.measures.communication_actions')}</span
														>
													</a>
													<a
														href="#/azionimonitoraggio"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">remove_red_eye</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.measures.monitoring_actions')}</span
														>
													</a>
												</div>
											{/if}
											<div class="{expanded ? 'group' : ''}">
												<span
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">map</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.investigations')}</span>
												</span>
												{#if $hasRoleSysAdmin}
													<a
														href="#/questionari"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">edit_note</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.investigations.basic_surveys')}</span
														>
													</a>
													<a
														href="#/questionariapprovazionearea"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">note_add</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.investigations.areas_surveys')}</span
														>
													</a>
													<a
														href="#/questionariaziendeenti"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">note_add</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.investigations.companies/entities_surveys')}</span
														>
													</a>
												{/if}
												{#if $hasRoleAreaManager}
													<a
														href="#/questionariapprovazionearea"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">note_add</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.investigations.surveys')}</span
														>
													</a>
													<a
														href="#/questionariaziendeenti"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">note_add</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate"
															>{$_('sidebar.investigations.companies/entities_surveys')}</span
														>
													</a>
												{/if}
												{#if $hasRoleCompanyManager}
													<a
														href="#/questionari"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">edit_note</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.investigations.surveys')}</span
														>
													</a>
												{/if}
											</div>
											<div class="{expanded ? 'group' : ''}">
												<span
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">summarize</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.reporting')}</span>
												</span>
												{#if $hasRoleAreaManager}
													<a
														href="#/questionarioaziendereport"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">poll</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">Report aziende/enti</span>
													</a>
													<a
														href="#/questionariosedireport"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">poll</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">Report sedi</span>
													</a>
													<a
														href="#/queries"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
													<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">query_stats</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">Query</span>
													</a>
												{/if}
												<a
													href="#/questionaririsposte"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">notes</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate"
														>{$_('sidebar.reporting.investigations_answers')}</span
													>
												</a>
											</div>
												{#if $hasRoleSysAdmin || $userSession?.scuola}
													<div class="{expanded ? 'group' : ''}">
														<span
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">supervised_user_circle</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.students')}</span>
														</span>
														<a
															href="#/indirizzidipendenti"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">person_pin_circle</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																>{$_('sidebar.students.addresses_import')}</span
															>
														</a>
														<a
															href="#/capcomunidipendenti"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">reduce_capacity</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																>{$_('sidebar.students.addresses_export')}</span
															>
														</a>
													</div>
												{/if}
											{/if}
											{#if $hasRoleCompanyManager || $hasRoleSysAdmin}
												<div class="{expanded ? 'group' : ''}">
													<span
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">route</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.build_the_plan')}</span>
													</span>
													{#if $hasRoleCompanyManager}
														<a
															href="#/psclmonitoraggimisure"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">track_changes</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																>Monitoraggio misure {lastYear}</span
															>
														</a>
														<a
															href="#/psclelaborazioni"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">table_chart</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																> Misure PSCL {currentYear}</span
															>
														</a>
													{/if}
													{#if $hasRoleSysAdmin || $hasRoleAreaManager || $hasRoleCompanyManager}
													<a
														href="#/calcoliemissioni"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">compost</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">Benefici ambientali</span>
													</a>
												{/if}
													{#if $hasRoleSysAdmin}
														<a
															href="#/psclschemabase"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">edit_location_alt</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																>PSCL Schema Base</span
															>
														</a>
														<a
															href="#/psclschemiarea"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">edit_location_alt</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																>PSCL Schema Area</span
															>
														</a>
													{/if}
													{#if $hasRoleCompanyManager}
														<a
															href="#/psclschemisede"
															class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none group-hover:flex relative flex-row items-center hidden pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
														>
															<span class="inline-flex items-center justify-center ml-4">
																<span class="material-icons-outlined">edit_location_alt</span>
															</span>
															<span class="ml-2 text-sm tracking-wide truncate"
																>{$_('sidebar.build_the_plan.movement_plans')}</span
															>
														</a>
													{/if}
												</div>
											{/if}
											{#if $hasRoleSysAdmin}
												<div>
													<a
														href="#/utenti"
														class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
													>
														<span class="inline-flex items-center justify-center ml-4">
															<span class="material-icons-outlined">people</span>
														</span>
														<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.users')}</span>
													</a>
												</div>
											{/if}
										{/if}
										{#if $userSession?.roles?.includes('User')}
											<div>
												<a
													href="#/questionaririsposte"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">notes</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">Questionari compilati</span>
												</a>
											</div>
										{/if}
										{#if !$userSession?.roles}
											<a
												href="#/news"
												class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 ml-6 text-gray-600 border-l-4 border-transparent"
											>
												<span class="inline-flex items-center justify-center ml-4">
													<span class="material-icons-outlined">newspaper</span>
												</span>
												<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.news')}</span>
											</a>
											<a
												href="#/indaginicompilate"
												class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
											>
												<span class="inline-flex items-center justify-center ml-4">
													<span class="material-icons-outlined">map</span>
												</span>
												<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.investigations_compiled')}</span>
											</a>
										{/if}
										{#if $hasRoleCompanyManager}
											<div>
												<a
													href="{$userSession?.supportUrl ? $userSession?.supportUrl : 'https://www.scrat-srl.it/mobility-platform/assistenza'}" target="_blank" rel="noreferrer"
													class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
												>
													<span class="inline-flex items-center justify-center ml-4">
														<span class="material-icons-outlined">contact_support</span>
													</span>
													<span class="ml-2 text-sm tracking-wide truncate">Assistenza</span>
												</a>
											</div>
										{/if}
										<div>
											<a
												href="#/changepassword"
												class="h-11 hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
											>
												<span class="inline-flex items-center justify-center ml-4">
													<span class="material-icons-outlined">lock</span>
												</span>
												<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.password_change')}</span>
											</a>
										</div>
										<div>
											<div
												role="button"
												tabindex="0"
												on:click="{async () => await signout()}"
												on:keydown="{async () => await signout()}"
												class="h-11 hover:cursor-pointer hover:border-mobilityPlatform-green hover:bg-gray-50 hover:text-gray-800 focus:outline-none relative flex flex-row items-center pr-6 text-gray-600 border-l-4 border-transparent"
											>
												<span class="inline-flex items-center justify-center ml-4">
													<svg
														class="w-5 h-5"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														xmlns="http://www.w3.org/2000/svg"
														><path
															stroke-linecap="round"
															stroke-linejoin="round"
															stroke-width="2"
															d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
														></path></svg
													>
												</span>
												<span class="ml-2 text-sm tracking-wide truncate">{$_('sidebar.logout')}</span>
											</div>
										</div>
									</div>
								</div>
								{#if companyLogo}
                                    <div class="flex-shrink-0 p-4 mt-auto">
                                        <img src="{companyLogo}" alt="Company Logo" class="h-auto max-w-full" />
                                    </div>
                                {/if}
							</div>
						</div>
					</div>
				</nav>
				<main
					id="main"
					class="{expanded ? 'sm:ml-48' : ''} mt-[4.2rem] mb-14 flex h-full w-full flex-col overflow-y-auto overflow-x-hidden bg-white"
				>
					<div class="flex w-full px-6 py-6 mx-auto">
						<div class="flex flex-col w-full h-full text-xl text-gray-900">
							<Content />
						</div>
					</div>
				</main>
			{:else}
				<main
					id="main"
					class="mt-[4.2rem] mb-14 flex h-full w-full flex-col overflow-y-auto overflow-x-hidden bg-white"
					style="background-image: url('assets/images/login-background-min.jpeg'); background-repeat: none; background-size:cover"
				>
					<div class="flex w-full px-6 py-6 mx-auto">
						<div class="flex flex-col w-full h-full text-xl text-gray-900">
							<Content />
						</div>
					</div>
				</main>
			{/if}
		</div>
	</div>
</div>